import { useState } from 'react';
import { useRecoilValue } from 'recoil';

import SortDropdown from '@Components/Shared/SortDropdown';
import TranslationText from '@Components/Utilities/TranslationText';
import { gtmPushEvent, chunkArray } from '@Components/Utilities/SandhillsUtility';
import { gtmEventType } from '@Components/Utilities/SandhillsConstants';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import translator from '@Components/Utilities/Translator';
import atoms from '@Components/Utilities/SandhillsAtoms';
import EmptyLegAlertModal from '@Components/Shared/Modals/EmptyLegAlertModal';

const MobileModalControl = loadable(() => import('@Components/Shared/Modals/MobileModalControl'));

const FacetedSearchMobileMenu = (props) => {

    const options = [];
    const windowSize = useWindowSize();
    const vip = sandhills.TradeSites.React.ComponentProps['vip'];
    const routes = useRecoilValue(atoms.routes);
    const currentUser = useRecoilValue(atoms.currentUser);
    const [showAlertModal, setShowAlertModal] = useState(false);

    if (props.flyout?.render) {
        options.push(
            <div className='option-bar-option black-background'>
                <MobileModalControl ClassicViewURL={props.flyout.url} ClassicViewImageURL={props.flyout.img} />
            </div>
        );
    }

    if (props.vip && vip) {
        options.push(
            <div className='option-bar-option black-background'>
                <a id='mobile-facet-vip-button' href={vip.VipPortalUrl} target='_blank' onClick={() => gtmPushEvent(gtmEventType.vip)}
                    aria-label={"VIP " + translator.translate('sandhills.tradesites.mvc.values') + " (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                    <img src={vip.VipPortalImage} className='option-bar-image vip-image' alt='VIP' width='40.5' height='15' />
                    <span className='uppercase'>
                        <TranslationText tKey='sandhills.tradesites.mvc.values' />
                    </span>
                </a>
            </div>
        );
    }

    if (props.hlaUrl && !props.IsRY) {
        options.push(
            <div className='option-bar-option'>
                <a href={props.hlaUrl} id='mobile-facet-hla-button'>
                    <i className='fas fa-long-arrow-alt-up' />
                    <i className='fas fa-long-arrow-alt-down' />
                    <span className='uppercase'>
                        <TranslationText tKey='sandhills.hilowavg' />
                    </span>
                </a>
            </div>
        );
    }

    if (props.watchList) {
        options.push(
            <div className='option-bar-option'>
                <a href={props.watchList} id='mobile-facet-watchlist-button'>
                    <i className='far fa-heart' />
                    <span className='uppercase'>
                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.watchlist' />
                    </span>
                </a>
            </div>
        );
    }

    if (props.PostEmptyLegUrl && props.ShowEmptyLegsButton) {
        options.push(
            <div className='option-bar-option'>
                <a href={props.PostEmptyLegUrl} id='mobile-facet-postemptyleg-button'>
                    <i className='fas fa-plus' />
                    <span className='uppercase'>
                        <TranslationText tKey='sandhills.postemptylegs' />
                    </span>
                </a>
            </div>
        );
    }

    if (props.IsEmptyLegsDirectory) {
        options.push(
            currentUser?.IsAuthenticated ?
                <div className='option-bar-option'>
                    <a role='button' id='mobile-facet-emptyleg-alerts-button' onClick={() => setShowAlertModal(true)}>
                        <i className='fa-solid fa-bell' />
                        <span className='uppercase'>Set Up Alerts</span>
                    </a>
                </div>
                :
                <div className='option-bar-option'>
                    <a id='mobile-facet-emptyleg-alerts-button' href={routes.EmptyLegsAlerts} target='_self'>
                        <i className='fa-solid fa-bell' />
                        <span className='uppercase'>Set Up Alerts</span>
                    </a>
                </div>
        );
    }

    if (props.stolenUrl) {
        options.push(
            <div className='option-bar-option'>
                <a id='mobile-facet-stolen-button' href={props.stolenUrl}>
                    <i className='fas fa-images' />
                    <span className='uppercase'>
                        <TranslationText tKey='sandhills.postastolenlisting' />
                    </span>
                </a>
            </div>
        );
    }

    if (props.sort?.options?.length > 0) {
        options.push(
            <SortDropdown
                toggleMobileSort={props.sort.toggle}
                ShowMobileSort={props.sort.render}
                SortOptions={props.sort.options}
                OnChange={props.sort.change}
                SelectedValue={props.sort.value}
                {...(props.setShowDistanceModal && { setShowDistanceModal: props.setShowDistanceModal })}
            />
        );
    }

    const columns = props.filter?.render ? 2 : 1;
    const chunkedOptions = chunkArray(options, columns);
    const chunkOneCount = chunkedOptions?.[0]?.length;
    const chunkTwoCount = chunkedOptions?.[1]?.length;
    const chunkOneBlack = (props.flyout?.render ? (chunkOneCount === 1 || ((props.vip && vip) && chunkOneCount === 2)) : ((props.vip && vip) && chunkOneCount === 1));
    const chunkTwoBlack = (props.flyout?.render && chunkOneCount === 1 && (props.vip && vip) && chunkTwoCount == 1);

    return (windowSize.isMobile ?
        (<>
            <div className='mobile-option-bar'>
                {props.filter?.render &&
                    <div className='mobile-option-bar-filter template-branded-button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); props.filter.toggle(); } }} onClick={props.filter.toggle}>
                        <div>
                            <i className='fas fa-filter' />
                            <i className='fas fa-sort small-sort' />
                            <span className='uppercase'>
                                <TranslationText tKey='SandhillsSystem.Filter' />
                            </span>
                        </div>
                    </div>}
                <div className='mobile-option-bar-options'>
                    {props.filter?.render ?
                        (<>
                            <div className={'mobile-options-group' + (chunkOneBlack ? ' black-background' : '')}>
                                {chunkedOptions?.[0]?.map(option => option)}
                            </div>
                            <div className={'mobile-options-group' + (chunkTwoBlack ? ' black-background' : '')}>
                                {chunkedOptions?.[1]?.map(option => option)}
                            </div>
                        </>) : (
                            <div className={'mobile-options-group' + (chunkOneBlack ? ' black-background' : '')}>
                                {chunkedOptions?.map(chunk => chunk?.map(option => option))}
                            </div>)}
                </div>
            </div>
            {showAlertModal &&
                <EmptyLegAlertModal
                    onClose={() => setShowAlertModal(false)}
                    onSave={() => setShowAlertModal(false)}
                />
            }
        </>) : (<></>)
    );
};

export default FacetedSearchMobileMenu;